<div data-testid="inbound-page" class="p-6 flex-table-page">
  <!-- Page Header -->
  <div class="mb-6 flex justify-between items-center flex-table-header">
    <h2 class="text-2xl font-semibold text-gray-800">
      {{ "inbound.title" | translate }}
    </h2>
  </div>

  <!-- Tabs -->
  <div class="mb-6 flex-table-header">
    <div class="border-b border-gray-200">
      <nav class="-mb-px flex space-x-8">
        <button
          data-testid="inbound-inbound-notice-tab-btn"
          (click)="setActiveTab('inbound-notice')"
          [class]="
            activeTab() === 'inbound-notice'
              ? 'border-blue-500 text-blue-600 border-b-2 py-2 px-1 text-sm font-medium'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 py-2 px-1 text-sm font-medium'
          "
        >
          {{ "inbound.tabs.inbound_notice" | translate }}
        </button>
        <button
          data-testid="inbound-inbound-warrant-tab-btn"
          (click)="setActiveTab('inbound-warrant')"
          [class]="
            activeTab() === 'inbound-warrant'
              ? 'border-blue-500 text-blue-600 border-b-2 py-2 px-1 text-sm font-medium'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 py-2 px-1 text-sm font-medium'
          "
        >
          {{ "inbound.tabs.inbound_warrant" | translate }}
        </button>
        <button
          data-testid="inbound-inbound-result-tab-btn"
          (click)="setActiveTab('inbound-result')"
          [class]="
            activeTab() === 'inbound-result'
              ? 'border-blue-500 text-blue-600 border-b-2 py-2 px-1 text-sm font-medium'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 border-b-2 py-2 px-1 text-sm font-medium'
          "
        >
          {{ "inbound.tabs.inbound_result" | translate }}
        </button>
      </nav>
    </div>
  </div>

  <!-- Inbound Warrant Tab Content -->
  @if (activeTab() === "inbound-warrant") {
    <!-- Search Form -->
    <app-search-form
      data-testid="inbound-warrant-search-form"
      [searchFormat]="searchFormat()"
      (formSearch)="onSearch($event)"
      (formReset)="onReset()"
    >
      <div slot="actions" class="flex gap-2">
        <app-button
          [testId]="'inbound-warrant-column-config-btn'"
          type="success"
          [outlined]="true"
          icon="view_column"
          (emitOnClick)="onColumnConfigClickWarrant($event)"
        ></app-button>
        <!-- Status Filter and Actions -->
        <app-status-filter
          [primaryButton]="primaryButton()"
          (primaryButtonClick)="onAddInboundNotice()"
        ></app-status-filter>
      </div>
    </app-search-form>

    <!-- Data Table -->
    <div class="table-section flex-table-section">
      <advanced-data-table
        data-testid="inbound-warrant-table"
        #advancedTableWarrant
        [dataSource]="dataSource()"
        [defaultColumns]="defaultColumns()"
        [flexibleColumns]="flexibleColumns()"
        [rows]="searchParams().size"
        [first]="(searchParams().page - 1) * searchParams().size"
        [totalRecords]="totalRecords()"
        [loading]="loading()"
        [sortField]="sortField()"
        [sortOrder]="sortOrder()"
        scrollHeight="flex"
        [showInternalColumnConfigButton]="false"
        class="w-full"
        (pageChange)="onPageChange($event)"
        (sortChange)="onSortChange($event)"
      >
        <!-- Custom Template for Inbound Type, Status, and Creation Time -->
        <ng-template
          #customTemplate
          let-value
          let-rowData="rowData"
          let-column="column"
        >
          @if (column.prop === "inputType") {
            <!-- Inbound Type -->
            <span class="text-xs text-gray-500">
              {{ getInboundTypeLabel(value) }}
            </span>
          } @else if (column.prop === "status") {
            <!-- Inbound Status -->
            <div class="flex items-center justify-center gap-1">
              <p-tag
                data-testid="inbound-warrant-status-tag"
                [value]="getInboundStatusLabel(value)"
                [severity]="getStatusSeverity(value)"
                styleClass="status-tag"
                [ngStyle]="getStatusStyle(value)"
                pTooltip="{{ getInboundStatusLabel(value) }}"
                tooltipPosition="top"
                tooltipStyleClass="custom-tooltip"
                [showDelay]="300"
                [hideDelay]="0"
              />
            </div>
          } @else if (column.prop === "creationDate") {
            <!-- Creation Time -->
            <span class="text-xs text-gray-500">
              {{ formatCreationTime(value) }}
            </span>
          } @else if (column.prop === "qty") {
            <!-- Required Inbound Quantity -->
            <span class="text-xs text-gray-500">
              {{ formatQuantity(value) }}
            </span>
          } @else {
            <!-- Default fallback -->
            <span class="text-xs text-gray-500">{{ value }}</span>
          }
        </ng-template>

        <!-- Actions Template -->
        <ng-template #actionsTemplate let-record>
          <div class="inline-flex items-center gap-2 whitespace-nowrap">
            <!-- View button -->
            @if (canRead()) {
              <app-button
                [testId]="'inbound-warrant-action-view-' + record.id"
                class="action-view"
                [label]="
                  'inbound.capabilities.inbound_warrant.actions.view'
                    | translate
                "
                type="action"
                (emitOnClick)="onViewDetails(record)"
              ></app-button>
            }

            <!-- Others button - only visible if there are actions after permission filtering -->
            @if (hasReceivingWarrantActions(record)) {
              <button
                [attr.data-testid]="'inbound-warrant-action-others-' + record.id"
                type="button"
                class="action-link action-others cursor-pointer"
                aria-haspopup="menu"
                (click)="toggleReceivingWarrantMenu($event, record)"
              >
                {{ "common.actions.others" | translate }}
              </button>
            }
          </div>
        </ng-template>
      </advanced-data-table>
    </div>
  }

  <!-- Inbound Notice Tab Content (Read-only) -->
  @if (activeTab() === "inbound-notice") {
    <!-- Search Form -->
    <app-search-form
      data-testid="inbound-notice-search-form"
      [searchFormat]="inboundNoticeSearchFormat()"
      (formSearch)="onInboundNoticeSearch($event)"
      (formReset)="onInboundNoticeReset()"
    >
      <div slot="actions">
        <app-button
          [testId]="'inbound-notice-column-config-btn'"
          type="success"
          [outlined]="true"
          icon="view_column"
          (emitOnClick)="onColumnConfigClickNotice($event)"
        ></app-button>
      </div>
    </app-search-form>

    <!-- Data Table -->
    <div class="table-section flex-table-section">
      <advanced-data-table
        data-testid="inbound-notice-table"
        #advancedTableNotice
        [dataSource]="inboundNoticeDataSource()"
        [defaultColumns]="inboundNoticeDefaultColumns()"
        [flexibleColumns]="inboundNoticeFlexibleColumns()"
        [rows]="inboundNoticeSearchParamsPagination().size"
        [first]="
          (inboundNoticeSearchParamsPagination().page - 1) *
          inboundNoticeSearchParamsPagination().size
        "
        [totalRecords]="inboundNoticeTotalRecords()"
        [loading]="inboundNoticeLoading()"
        [sortField]="inboundNoticeSortField()"
        [sortOrder]="inboundNoticeSortOrder()"
        scrollHeight="flex"
        [showInternalColumnConfigButton]="false"
        class="w-full"
        (pageChange)="onInboundNoticePageChange($event)"
        (sortChange)="onInboundNoticeSortChange($event)"
      >
        <!-- Custom Template for Status and Creation Time -->
        <ng-template
          #customTemplate
          let-value
          let-rowData="rowData"
          let-column="column"
        >
          @if (column.prop === "status") {
            <!-- Inbound Status -->
            <div class="flex items-center justify-center gap-1">
              <p-tag
                data-testid="inbound-notice-status-tag"
                [value]="getInboundStatusLabel(value)"
                [severity]="getStatusSeverity(value)"
                styleClass="status-tag"
                [ngStyle]="getStatusStyle(value)"
                pTooltip="{{ getInboundStatusLabel(value) }}"
                tooltipPosition="top"
                tooltipStyleClass="custom-tooltip"
                [showDelay]="300"
                [hideDelay]="0"
              />
            </div>
          } @else if (column.prop === "creationDate") {
            <!-- Creation Time -->
            <span class="text-xs text-gray-500">
              {{ formatCreationTime(value) }}
            </span>
          } @else if (column.prop === "warehouseCode") {
            <!-- Warehouse Code -->
            <span class="text-xs text-gray-700">
              {{ value || "-" }}
            </span>
          } @else if (column.prop === "warehouseName") {
            <!-- Warehouse Name -->
            <span class="text-xs text-gray-700">
              {{ value || "-" }}
            </span>
          } @else if (
            column.prop === "expectedArrivalDate" ||
            column.prop === "deliveryDate"
          ) {
            <!-- Expected Arrival Date -->
            <span class="text-xs text-gray-700">
              {{ value ? formatDateOnly(value) : "-" }}
            </span>
          } @else if (
            column.prop === "remarks" || column.prop === "noticeTypeDescription"
          ) {
            <!-- Remarks / Notice Type Description -->
            <span class="text-xs text-gray-700" [title]="value">
              {{
                value
                  ? value.length > 50
                    ? value.substring(0, 50) + "..."
                    : value
                  : "-"
              }}
            </span>
          } @else if (column.prop === "lastUpdateDate") {
            <!-- Last Update Time -->
            <span class="text-xs text-gray-500">
              {{ formatCreationTime(value) }}
            </span>
          } @else if (column.prop === "inputType") {
            <!-- Inbound Type -->
            <span class="text-xs text-gray-700">
              {{ getInboundTypeLabel(value) }}
            </span>
          } @else {
            <!-- Default fallback -->
            <span class="text-xs text-gray-500">{{ value }}</span>
          }
        </ng-template>

        <!-- Actions Template -->
        <ng-template #actionsTemplate let-record>
          <div class="inline-flex items-center gap-2 whitespace-nowrap">
            <!-- View button -->
            @if (canRead()) {
              <app-button
                [testId]="'inbound-notice-action-view-' + record.id"
                class="action-view"
                [label]="'common.actions.view' | translate"
                type="action"
                (emitOnClick)="onInboundNoticeViewDetails(record)"
              ></app-button>
            }

            <!-- Others button -->
            @if (getInboundNoticeActions(record).length > 0) {
              <button
                [attr.data-testid]="'inbound-notice-action-others-' + record.id"
                type="button"
                class="action-link action-others cursor-pointer"
                aria-haspopup="menu"
                (click)="toggleInboundNoticeMenu($event, record)"
              >
                {{ "common.actions.others" | translate }}
              </button>
            }
          </div>
        </ng-template>
      </advanced-data-table>
    </div>
  }

  <!-- Warehouse Result Sheet Tab Content -->
  @if (activeTab() === "inbound-result") {
    <!-- Search Form for Result Sheet -->
    <app-search-form
      data-testid="inbound-result-search-form"
      [searchFormat]="warehouseResultSearchFormat()"
      (formSearch)="onWarehouseResultSearch($event)"
      (formReset)="onWarehouseResultReset()"
    >
      <div slot="actions">
        <app-button
          [testId]="'inbound-result-column-config-btn'"
          type="success"
          [outlined]="true"
          icon="view_column"
          (emitOnClick)="onColumnConfigClickResult($event)"
        ></app-button>
      </div>
    </app-search-form>

    <!-- Data Table for Result Sheet -->
    <div class="table-section flex-table-section">
      <advanced-data-table
        data-testid="inbound-result-table"
        #advancedTableResult
        [dataSource]="warehouseResultDataSource()"
        [defaultColumns]="warehouseResultDefaultColumns()"
        [flexibleColumns]="warehouseResultFlexibleColumns()"
        [rows]="warehouseResultSearchParamsPagination().size"
        [first]="
          (warehouseResultSearchParamsPagination().page - 1) *
          warehouseResultSearchParamsPagination().size
        "
        [totalRecords]="warehouseResultTotalRecords()"
        [loading]="warehouseResultLoading()"
        [sortField]="warehouseResultSortField()"
        [sortOrder]="warehouseResultSortOrder()"
        scrollHeight="flex"
        [showInternalColumnConfigButton]="false"
        class="w-full"
        (pageChange)="onWarehouseResultPageChange($event)"
        (sortChange)="onWarehouseResultSortChange($event)"
      >
        <!-- Custom Template for Inbound Type, Status, and Creation Time -->
        <ng-template
          #customTemplate
          let-value
          let-rowData="rowData"
          let-column="column"
        >
          @if (column.prop === "inputType") {
            <!-- Inbound Type -->
            <span class="text-xs text-gray-500">
              {{ getInboundTypeLabel(value) }}
            </span>
          } @else if (column.prop === "creationDate") {
            <!-- Creation Date -->
            <span class="text-xs text-gray-600">
              {{ formatCreationTime(value) }}
            </span>
          } @else {
            <!-- Default fallback -->
            <span class="text-xs text-gray-500">{{ value }}</span>
          }
        </ng-template>

        <!-- Actions Template for Warehouse Result -->
        <ng-template #actionsTemplate let-record>
          <div class="flex gap-2 justify-center">
            @if (canRead()) {
              <app-button
                [testId]="'inbound-result-action-view-' + record.id"
                class="action-view"
                [label]="
                  'inbound.capabilities.inbound_result.actions.view' | translate
                "
                type="action"
                (emitOnClick)="onWarehouseResultViewDetails(record)"
              ></app-button>
            }
          </div>
        </ng-template>
      </advanced-data-table>
    </div>
  }
</div>

<!-- Single Menu for Inbound Notice Actions -->
<p-menu
  data-testid="inbound-notice-actions-menu"
  #inboundNoticeActionMenu
  [popup]="true"
  [model]="currentInboundNoticeMenuModel()"
  appendTo="body"
  [baseZIndex]="2100"
  styleClass="actions-menu text-[0.75rem]"
  [autoZIndex]="true"
></p-menu>

<!-- Single Menu for Receiving Warrant Actions -->
<p-menu
  data-testid="inbound-warrant-actions-menu"
  #inboundWarrantActionMenu
  [popup]="true"
  [model]="currentInboundWarrantMenuModel()"
  appendTo="body"
  [baseZIndex]="2100"
  styleClass="actions-menu text-[0.75rem]"
  [autoZIndex]="true"
></p-menu>

<!-- Confirmation Dialog for Cancel -->
<app-confirmation-dialog
  [testId]="'inbound-cancel-dialog'"
  [visible]="showCancelDialog()"
  [title]="'inbound.dialog.confirm_cancel.title' | translate"
  [message]="'inbound.dialog.confirm_cancel.message' | translate"
  [buttons]="cancelDialogButtons"
  (emitOnClick)="onCancelConfirm($event)"
></app-confirmation-dialog>

<!-- Toast messages -->
<p-toast data-testid="inbound-toast"></p-toast>
